import { MALE_PARTNER_TREATMENT_VOUCHER_SLUG } from "@/components/care/aLaCare/MalePartnerTreatmentVoucherCard";
import {
  ENTRY_POINT_PARAM,
  SHOP_TREATMENTS_URL,
  TreatmentPlanSurface,
  VIEW_RX_PRODUCTS,
} from "@/components/care/constants";
import ApplicatorCreamImg from "@/components/common/images/treatments/evvy-cream-applicator.png";
import CreamImg from "@/components/common/images/treatments/evvy-cream.png";
import PillsImg from "@/components/common/images/treatments/evvy-pill-bottle.png";
import SuppositoriesImg from "@/components/common/images/treatments/evvy-suppositories.png";
import { GENERAL_COACHING_CALL_LINK } from "@/pages/constants";
import { careService } from "@/services/care";
import {
  Consult,
  Prescription,
  PrescriptionTag,
  TreatmentFormFactor,
  TreatmentGroups,
  TreatmentPhase,
  TreatmentPurposeTag,
} from "@/types/care";
import { Test } from "@/types/test";
import {
  sendClickedCareCTA,
  sendClickedSTIIntakeCTA,
} from "@/utils/analytics/customEventTracking";

const DISRUPTIVE_DESCRIPTION = "Reduce disruptive bacteria";
const PROTECTIVE_DESCRIPTION = "Promote protective bacteria";
const MAINTENANCE_DESCRIPTION = "Maintain & soothe symptoms";

export const getButtonCTATextForConsultation = (consult: Consult): string => {
  if (!consult) return `Get started →`;
  if (consult.status === "CP")
    return consult.purchase_type === "a-la-care"
      ? VIEW_RX_PRODUCTS
      : "View treatment program →";
  if (consult.consult_pending) return "View consultation status →";
  if (consult.consult_paid) return "Complete intake →";

  // catch all
  return `Get started →`;
};

export const openCoachingLink = () => {
  window.open(GENERAL_COACHING_CALL_LINK, "_blank");
};

// This is meant to handle v0-bundle and care ineligible care intro clicks.
// Not meant for use for a la care consults
// This code is only used for the coaching intro now after treatment recommendations overhaul
export const handleCoachingIntroCTAClick = (
  ctaText: string,
  location: string,
  test?: Test
) => {
  // if ineligible for care, then take them to coaching
  sendClickedCareCTA({
    location,
    ctaText,
    testHash: test?.hash,
    testOrder: test?.test_order,
    carePrice: test?.eligible_for_care
      ? test?.recommended_program_bundle_price
      : null,
    careEligible: test?.eligible_for_care,
    valenciaType: test?.valencia_type,
    treatmentPathway: test?.recommended_program_slug,
  });

  return openCoachingLink();
};

export const getPhasesFromPrescriptions = (
  prescriptions: Prescription[],
  includeMaintenance: boolean = false
) => {
  const phases: TreatmentPhase[] = [];

  prescriptions.forEach((prescription) => {
    // skip if no tag
    if (!prescription.tag) {
      return;
    }

    // skip this prescription if we already have this phase
    if (phases.map((p) => p.tag).includes(prescription.tag)) {
      return;
    }

    // new phase, let's put it in
    phases.push({
      number: phases.length + 1,
      tag: prescription.tag,
      goal:
        prescription.tag === PrescriptionTag.REDUCE_DISRUPTIVE
          ? DISRUPTIVE_DESCRIPTION
          : prescription.tag === PrescriptionTag.PROMOTE_PROTECTIVE
            ? PROTECTIVE_DESCRIPTION
            : MAINTENANCE_DESCRIPTION,
      bgClass: getTagBackgroundClass(prescription.tag),
    });
  });

  // if we want to include maintenance, then add it if it's not already there
  const maintenanceTag = PrescriptionTag.MAINTENANCE;
  if (includeMaintenance) {
    if (!phases.map((p) => p.tag).includes(maintenanceTag)) {
      phases.push({
        number: phases.length + 1,
        tag: maintenanceTag,
        goal: MAINTENANCE_DESCRIPTION,
        bgClass: getTagBackgroundClass(maintenanceTag),
      });
    }
  }

  return phases;
};

export const getTagBackgroundClass = (tag: string) => {
  if (tag === "Reduce Disruptive") {
    return "from-BA-gradientLight to-BA-oldFill";
  } else if (tag === "Promote Protective") {
    return "from-GO-gradientLight to-GO-oldFill";
  } else if (tag === "Maintenance") {
    return "from-NE-gradientLight to-NE-oldFill";
  } else {
    return "";
  }
};

export const createAndGoToSTIConsult = async (testHash: string) => {
  try {
    const consultResponse = await careService.asyncCreateConsult({
      hash: testHash,
      type: "sti",
      pharmacy_type: "patient-local-pickup",
    });

    const consult = consultResponse.data;
    window.location.href = `/treatments/sti/consults/${consult.uid}/intake/`;
  } catch (error: any) {
    console.error("Error creating STI consult:", error);
    // Show user-friendly error message
    const errorMessage =
      error.response?.data?.detail ||
      error.message ||
      "Unable to start STI treatment process. Please try again or contact support.";
    alert(`Error: ${errorMessage}`);
  }
};

export const handleSTIConsultCTAClick = async (
  testHash: string,
  analyticsEventArgs: any,
  latestSTIConsult?: Consult
) => {
  const basePath = "/treatments";
  sendClickedSTIIntakeCTA(analyticsEventArgs);

  try {
    if (latestSTIConsult) {
      window.location.href = `${basePath}/sti/consults/${latestSTIConsult.uid}/intake/`;
    } else {
      await createAndGoToSTIConsult(testHash);
    }
  } catch (error: any) {
    console.error("Error handling STI consult CTA click:", error);
    // Error handling is already done in createAndGoToSTIConsult
  }
};

export const getTreatmentImageFromFormFactor = (
  formFactor?: TreatmentFormFactor
) => {
  const FORM_FACTOR_TO_IMAGE = {
    pill: PillsImg,
    cream: CreamImg,
    "applicator-cream": ApplicatorCreamImg,
    suppository: SuppositoriesImg,
  };

  if (formFactor) {
    return FORM_FACTOR_TO_IMAGE[formFactor];
  } else {
    return PillsImg;
  }
};

export const getTreatmentPurposeTagColor = (
  purposeTag?: TreatmentPurposeTag
) => {
  const PURPOSE_TAG_TO_BG_COLOR = {
    rebuild: "bg-evvy-silverfish",
    relief: "bg-highlighter-mint",
    treat: "bg-highlighter-salmon",
  };

  return purposeTag ? PURPOSE_TAG_TO_BG_COLOR[purposeTag] : "bg-evvy-blue";
};

// Server-resolved money arrives cent-precise ("111.60"); show the cents when present
// instead of rounding to the dollar like the catalog-price formatter below.
// Server prices arrive cent-precise; render them as sent. Never formats a missing price as money.
export const formatResolvedPriceString = (price: string | number | null) => {
  if (price == null) return "";
  return Number(price).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const formatNumberToPriceString = (price: number) => {
  const roundedPrice = Math.round(Number(price));
  return roundedPrice.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: roundedPrice % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
};

export const formatNumberStringToPriceString = (price: string) => {
  return formatNumberToPriceString(Number(price));
};

export const getShopUrlFromCareProduct = (product: Prescription) => {
  // Special handling for male partner treatment voucher
  if (product.slug === "male-partner-voucher") {
    return `${SHOP_TREATMENTS_URL}/product/${MALE_PARTNER_TREATMENT_VOUCHER_SLUG}/`;
  }
  // New PDP routes
  if (product.pdp_slug) {
    return `${SHOP_TREATMENTS_URL}/product/${product.pdp_slug}/`;
  }
  return `${SHOP_TREATMENTS_URL}/${product.slug}/`;
};

export const getTreatmentPlanUrlFromConsult = (consult?: Consult) => {
  return `/treatments/consults/${consult?.uid}/treatment/`;
};

export type CareIntakeStep =
  | "intro"
  | "eligibility"
  | "demographics"
  | "identity-verification/redo"
  | "identity-verification/reupload"
  | "consent"
  | "allergies"
  | "start"; // Added for male partner intake

export const getCareIntakeUrl = (
  intakeStep: CareIntakeStep,
  consult: Consult
) => {
  if (consult?.type === "male-partner") {
    return `/treatments/consults/${consult?.uid}/male-partner/intake/${intakeStep}/`;
  }
  if (consult?.type === "ungated-rx") {
    return `/treatments/rx/consults/${consult?.uid}/intake/${intakeStep}/`;
  }
  if (consult?.type === "uti") {
    return `/treatments/uti/consults/${consult?.uid}/intake/${intakeStep}/`;
  }
  return `/treatments/consults/${consult?.uid}/intake/${intakeStep}/`;
};

export const getCarePageVisitedLocalStorageKey = (testHash: string) => {
  return `visited-care-page-${testHash}`;
};

export const getPurposeTagText = (purpose: string) => {
  if (purpose === "rebuild") {
    return "Protect";
  }
  return purpose;
};

export const extractAllSlugsFromTreatmentGroups = (
  treatmentGroups: TreatmentGroups | undefined
): string[] => {
  if (!treatmentGroups) return [];

  const slugs: string[] = [];
  Object.values(treatmentGroups).forEach((phase) => {
    phase.treatments.forEach((treatment) => {
      if (treatment.slug) {
        slugs.push(treatment.slug);
      }
    });
  });
  return slugs;
};

/**
 * Get the correct navigation URL for a product based on whether it has a PDP configuration
 * @param product - The product/treatment object
 * @returns The URL to navigate to
 */
export const getProductNavigationUrl = (
  useNewPDP: boolean,
  productSlug?: string,
  treatmentSlug?: string,
  isTest?: boolean
): string => {
  // If the product has a PDP configuration, use the PDP slug
  if (productSlug && useNewPDP && !isTest) {
    return `/care/shop/treatment/product/${productSlug}`;
  }
  if (productSlug && useNewPDP && isTest) {
    return `/care/shop/test/product/${productSlug}`;
  }
  if (isTest) {
    return `/care/shop/test/${treatmentSlug}`;
  }
  // Fall back to the traditional treatment URL
  return `/care/shop/treatment/${treatmentSlug}/`;
};

/** Marks a PDP url as entered from `surface`'s treatment plan, so the PDP can show the program sticky CTA. */
export const withTreatmentPlanEntryPoint = (
  url: string,
  surface: TreatmentPlanSurface
): string => {
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}${ENTRY_POINT_PARAM}=${surface}`;
};
